import type { AccessPolicy, AccessPolicyUpdate } from "./_shared"

import { api } from "@/lib/api-client"

/**
 * What happened to the policy push that follows a change to a `personal_source_*`
 * feature. Anything but `applied` means FILTH is still enforcing the previous policy.
 */
export type PersonalPolicyPush = {
  status:
    | "applied"
    | "confirmation_required"
    | "skipped_read_failed"
    | "skipped_unreadable"
    | "filth_unavailable"
    | "refused"
    | "transient_error"
  policy_version: number
  expected_hidden?: number
  /** The policy snapshot FILTH holds back (with `confirmation_required`); a confirmation must name it. */
  policy_digest?: string
}

export type AdminFeatureItem = {
  id: string
  name: string
  description: string
  enabled: boolean
  access: AccessPolicy
  access_configured?: boolean
  access_overridden?: boolean
}

export async function getAdminFeatures(): Promise<AdminFeatureItem[]> {
  const response = await api.get("/admin/features")
  const data = response.data || response
  return Array.isArray(data) ? data : []
}

export async function updateFeatureStatus(
  featureId: string,
  enabled: boolean
): Promise<{
  success: boolean
  feature_id: string
  enabled: boolean
  message: string
  policy_push?: PersonalPolicyPush
}> {
  const response = await api.put(`/admin/features/${featureId}/status`, { enabled })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to update feature ${featureId} status`)
  }

  return data
}

export async function updateFeaturesStatusBatch(updates: Record<string, boolean>): Promise<{
  success: boolean
  updated_features: string[]
  message: string
  policy_push?: PersonalPolicyPush
}> {
  const response = await api.put("/admin/features/status/batch", { updates })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || "Failed to batch update feature statuses")
  }

  return data
}

export async function getFeatureBaseAccessPolicy(): Promise<AccessPolicy> {
  const response = await api.get("/admin/features/base/access")
  const data = response.data || response
  return data.access || {}
}

export async function updateFeatureBaseAccessPolicy(
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put("/admin/features/base/access", policy)
  const data = response.data || response
  return data.access || {}
}

export async function clearFeatureAccessPolicy(featureId: string): Promise<{
  access: AccessPolicy
  policy_push?: PersonalPolicyPush
}> {
  const response = await api.put(`/admin/features/${featureId}/access`, {})
  const data = response.data || response
  return { access: data.access || {}, policy_push: data.policy_push }
}

export async function getFeatureAccessPolicy(featureId: string): Promise<AccessPolicy> {
  const response = await api.get(`/admin/features/${featureId}/access`)
  const data = response.data || response
  return data.access || {}
}

export async function updateFeatureAccessPolicy(
  featureId: string,
  policy: AccessPolicyUpdate
): Promise<{ access: AccessPolicy; policy_push?: PersonalPolicyPush }> {
  const response = await api.put(`/admin/features/${featureId}/access`, policy)
  const data = response.data || response
  return { access: data.access || {}, policy_push: data.policy_push }
}

// =============================================================================
// Feature Configuration API
// =============================================================================

export async function fetchFeatureConfig<TConfig = Record<string, unknown>>(
  featureId: string
): Promise<{ feature_id: string; config: TConfig }> {
  const response = await api.get(`/admin/features/${featureId}/config`)
  return response.data || response
}

export async function updateFeatureConfig<
  TConfig extends Record<string, unknown> = Record<string, unknown>,
>(
  featureId: string,
  config: TConfig,
  options?: {
    reason?: string
    source?: "form" | "raw"
    /**
     * Personal sources only: raising `history_window_days` indexes older items, so
     * the server refuses it (409 `widen_confirmation_required`) unless this is true.
     */
    widenConfirmed?: boolean
  }
): Promise<{
  feature_id: string
  config: TConfig
  message: string
  policy_push?: PersonalPolicyPush
}> {
  const body: Record<string, unknown> = { config }
  if (options?.source) body.source = options.source
  if (options?.reason) body.reason = options.reason
  if (options?.widenConfirmed === true) body.widen_confirmed = true
  const response = await api.put(`/admin/features/${featureId}/config`, body)
  return response.data || response
}
